import { gql } from '@apollo/client';
import { Alert, Badge, Button, Col, Form, Input, Row, Select } from 'antd';
import FormLabel from 'components/FormLabel';
import FormDependency, { shouldUpdateDependency } from 'components/FormDependency';
import { useMutation } from 'hooks/apollo';
import React, { useEffect } from 'react';
import theme from 'shared/constants/theme.json';
import ActiveInput from 'components/ActiveInput';
import { DeleteFilled, NumberOutlined, PlusOutlined } from '@ant-design/icons';
import useDataSource from 'hooks/useDataSource';
import Page from 'components/Page';
import { FormattedMessage } from 'react-intl';
import messages from '../messages';

const FIELDS = `
  id
  matomoId
  matomoActionDimensions {
    id
    index
    name
    active
    extractions {
      dimension
      pattern
    }
  }
  matomoCustomDimensionScopes {
    id
    numSlotsAvailable    
  }
`;

const extraFields = `
  website {
    ${FIELDS}
  }
`;

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      ${FIELDS}
    }
  }
`;

const extractionOptions = [
  { value: 'url', label: 'Page URL' },
  { value: 'urlparam', label: 'Page URL Parameter' },
  { value: 'action_name', label: 'Page Title' },
];

const ActionDimensionExtractions = ({ name }) => (
  <Form.Item label={<FormLabel label={messages.actionDimensionExtractionsLabel} description={messages.actionDimensionExtractionsDescription} />}>
    <Form.List name={[name, 'extractions']}>
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <Row key={field.key} gutter={16}>
              <Col span={8}>
                <Form.Item name={[field.name, 'dimension']} rules={[{ required: true, message: 'Extraction type is required' }]}>
                  <Select options={extractionOptions} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item noStyle shouldUpdate={shouldUpdateDependency(['matomoActionDimensions', name, 'extractions', field.name, 'dimension'])}>
                  {({ getFieldValue }) => {
                    const dimension = getFieldValue(['matomoActionDimensions', name, 'extractions', field.name, 'dimension']);
                    return (
                      <Form.Item name={[field.name, 'pattern']} rules={[{ required: true, pattern: dimension !== 'urlparam' ? /.*\(.+\).*/ : null, message: 'Capture group is required' }]}>
                        <Input suffix={<DeleteFilled className="ant-btn-dangerous" onClick={() => remove(field.name)} />} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Form.Item>
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
              Add extraction
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  </Form.Item>
);

const ActionDimensionSettings = () => {
  const { dataSource, loading, refetch } = useDataSource({ extraFields });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE, { onCompleted: refetch });
  const [form] = Form.useForm();

  const { website } = dataSource || {};
  const websiteId = website?.id;

  useEffect(() => {
    form.resetFields();
  }, [website]);

  const onSubmit = (values) => updateWebsite({ variables: { id: websiteId, input: values } });

  const { numSlotsAvailable: actionSlotsAvailable = 0 } = website?.matomoCustomDimensionScopes?.find(s => s.id === 'action') || {};
  const actionDimensionCount = website?.matomoActionDimensions?.length || 0;

  return (
    <Page loading={loading}>
      <Alert
        message={(
          <a href="https://academy.extellio.com/knowledge-base/analytics/how-to-create-custom-dimensions" target="_blank">Read more about custom dimensions in our guide.</a>
        )}
        type="info"
        showIcon
      />
      <Page.Content title="Action dimensions" extra={<Badge style={{ backgroundColor: theme[(actionSlotsAvailable >= 0 && actionDimensionCount >= actionSlotsAvailable) ? 'error-color' : 'success-color'] }} count={`${actionDimensionCount} / ${actionSlotsAvailable}`} showZero />}>
        <Form form={form} onFinish={onSubmit} initialValues={website} layout="vertical">
          <p><FormattedMessage {...messages.matomoActionDimensionsDescription} /></p>
          <Form.List name="matomoActionDimensions">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row key={field.key} gutter={16}>
                    <Col span={3}>
                      <Form.Item name={[field.name, 'index']}>
                        <Input prefix={<NumberOutlined />} disabled />
                      </Form.Item>
                    </Col>
                    <Col span={19}>
                      <Form.Item name={[field.name, 'name']} rules={[{ required: true, message: 'Dimension name is required' }]}>
                        <Input placeholder="Name" suffix={<FormDependency name={['matomoActionDimensions', field.name, 'id']} show={v => !v}><DeleteFilled className="ant-btn-dangerous" onClick={() => remove(field.name)} /></FormDependency>} />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Form.Item name={[field.name, 'active']}>
                        <ActiveInput />
                      </Form.Item>
                    </Col>
                    <Col offset={1} span={23}>
                      <ActionDimensionExtractions name={field.name} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!website || fields.length >= actionSlotsAvailable}>
                  Add action dimension
                </Button>
              </>
            )}
          </Form.List>

          <Page.Footer>
            <Button type="primary" htmlType="submit" loading={updating}>Save</Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default ActionDimensionSettings;
